import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  input,
  output,
  inject,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NonNullableFormBuilder,
  ReactiveFormsModule,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime } from 'rxjs';
import {
  DEFAULT_DEBOUNCE_TIME_FOR_SEARCH_FIELD_IN_MS,
  Resetable,
} from '../../models/global.model';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'ri-resolution-ui-search-bar',
  imports: [MatIconModule, MatTooltipModule, ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './search-bar.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchBarComponent),
      multi: true,
    },
  ],
})
export class SearchBarComponent implements Resetable, ControlValueAccessor {
  private readonly fb = inject(NonNullableFormBuilder);

  // Input variables
  readonly placeholder = input<string>('Search');
  readonly delay = input<number>(DEFAULT_DEBOUNCE_TIME_FOR_SEARCH_FIELD_IN_MS);

  // Output variables
  readonly searchStringChange = output<string>();

  readonly tooltipMessage = `Pending tasks appear first, ordered by most recent`;
  public readonly searchStringControl = this.fb.control<string>('');

  // eslint-disable-next-line  @typescript-eslint/no-unused-vars
  onChange = (value: string) => {};
  onTouched = () => {};

  constructor() {
    this.searchStringControl.valueChanges
      .pipe(debounceTime(this.delay()), takeUntilDestroyed())
      .subscribe((searchString) => {
        this.searchStringChange.emit(searchString);
        this.onChange(searchString);
      });
  }

  resetState(): void {
    this.searchStringControl.setValue('');
  }

  listenForEsc($event: KeyboardEvent) {
    if ($event.key.toLocaleLowerCase() === 'escape') {
      this.resetState();
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  writeValue(parentComponentValue: string): void {
    this.searchStringControl.setValue(parentComponentValue);
  }
}
